import { useState, type FormEvent } from "react";
import type { ProjectRole, WorkspaceRole } from "@melo/shared";
import { DISCIPLINES, DISCIPLINE_LABELS } from "@melo/shared/client";
import { api, ApiError } from "../lib/api";
import { useTables } from "../lib/store";
import { spacesSorted } from "../lib/select";
import { can, isAdmin, ROLE_HINT, ROLE_LABEL } from "../lib/access";
import { toast } from "../lib/toast";
import { Dialog, SpaceDot } from "./primitives";
import { Icon } from "./icons";

/** Makes a single-use invite link. Admins can invite anyone; producers invite to their own projects. */
export function InviteDialog({ onClose, projectId, onCreated }: { onClose: () => void; projectId?: string; onCreated?: () => void }) {
  const t = useTables();
  const admin = isAdmin();
  const projects = spacesSorted(t).filter((s) => admin || can(s.id, "producer"));
  const [note, setNote] = useState("");
  const [role, setRole] = useState<WorkspaceRole>("contractor");
  const [disciplines, setDisciplines] = useState<string[]>([]);
  const [picked, setPicked] = useState<Record<string, ProjectRole>>(projectId ? { [projectId]: "contractor" } : {});
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState("");
  const roles: WorkspaceRole[] = admin ? ["contractor", "member", "admin"] : ["contractor", "member"];

  const setWorkspaceRole = (r: WorkspaceRole) => {
    setRole(r);
    // Contractors are contractors in every project; others default to member.
    setPicked(Object.fromEntries(Object.keys(picked).map((id) => [id, r === "contractor" ? "contractor" : picked[id] === "contractor" ? "member" : picked[id]!])));
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res = await api.post<{ url: string }>("/v1/invites", {
        note: note.trim(),
        role,
        disciplines,
        spaces: Object.entries(picked).map(([spaceId, r]) => ({ spaceId, role: r })),
      });
      setLink(res.url);
      onCreated?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the invite.");
    }
  };

  if (link)
    return (
      <Dialog title="Invite link" onClose={onClose}>
        <div className="form">
          <p className="hint">Send this to {note || "them"}. It works once and expires in 7 days. They sign in with Roblox; they don't need to be in the Melo group.</p>
          <pre className="code">{link}</pre>
          <div className="form-actions">
            <button
              type="button"
              className="btn pri"
              onClick={() =>
                navigator.clipboard.writeText(link).then(
                  () => toast("Invite link copied"),
                  () => toast("Could not copy. Select the link instead.", { tone: "error" }),
                )
              }
            >
              <Icon name="copy" /> Copy link
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </Dialog>
    );

  return (
    <Dialog title="Invite someone" onClose={onClose} wide>
      <form className="form" onSubmit={submit}>
        <div className="field-row">
          <label className="label" htmlFor="inv-note">Who is it for</label>
          <input id="inv-note" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="For example: Kofi, ship modeler" />
          <span className="hint">Only you and admins see this note.</span>
        </div>
        <div className="field-row">
          <span className="label">Role</span>
          <div className="choice-list" role="radiogroup" aria-label="Role">
            {roles.map((r) => (
              <label key={r} className={`choice${role === r ? " on" : ""}`}>
                <input type="radio" name="inv-role" checked={role === r} onChange={() => setWorkspaceRole(r)} />
                <b>{ROLE_LABEL[r]}</b>
                <span className="muted">{ROLE_HINT[r]}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="field-row">
          <span className="label">What they do</span>
          <div className="tags" role="group" aria-label="Disciplines">
            {DISCIPLINES.map((d) => (
              <button key={d} type="button" className={`chip${disciplines.includes(d) ? " agent" : ""}`} aria-pressed={disciplines.includes(d)} onClick={() => setDisciplines(disciplines.includes(d) ? disciplines.filter((x) => x !== d) : [...disciplines, d])}>
                {DISCIPLINE_LABELS[d]}
              </button>
            ))}
          </div>
        </div>
        {role !== "admin" && (
          <div className="field-row">
            <span className="label">Projects</span>
            <div className="proj-pick">
              {projects.map((s) => {
                const on = picked[s.id] !== undefined;
                return (
                  <div key={s.id} className={`proj-row${on ? " on" : ""}`}>
                    <label className="check grow">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => {
                          const next = { ...picked };
                          if (e.target.checked) next[s.id] = role === "contractor" ? "contractor" : "member";
                          else delete next[s.id];
                          setPicked(next);
                        }}
                      />
                      <SpaceDot space={s} /> {s.name}
                    </label>
                    {on && role !== "contractor" && (
                      <select className="select" style={{ width: 150 }} aria-label={`Role in ${s.name}`} value={picked[s.id]} onChange={(e) => setPicked({ ...picked, [s.id]: e.target.value as ProjectRole })}>
                        <option value="member">Member</option>
                        <option value="producer">Producer</option>
                      </select>
                    )}
                  </div>
                );
              })}
              {!projects.length && <p className="hint">You don't produce any projects yet.</p>}
            </div>
          </div>
        )}
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={role !== "admin" && !admin && !Object.keys(picked).length}>
            Create invite link
          </button>
        </div>
      </form>
    </Dialog>
  );
}
